/**
 * YearCalendarForm — génère toutes les périodes de paie de l'année (brouillon) en un clic.
 * Fériés du Québec identifiés par période ; paie tombant un férié / fin de semaine → jour ouvrable précédent.
 */
import { useEffect, useState } from "react";
import axios from "axios";
import { AlertTriangle, CalendarClock, X, Eye, Sparkles } from "lucide-react";
import { toast } from "@/lib/toast";
import { API, authH, FREQ_LABELS, inputCls, Field, Btn, Check, err } from "./_shared";

export default function YearCalendarForm({ masterId, year, defaultFrequency, onDone, onCancel }) {
  const [f, setF] = useState({ frequency: defaultFrequency || "biweekly", first_period_start: `${year - 1}-12-28`, first_pay_date: `${year}-01-08`, shift_holidays: true });
  const [preview, setPreview] = useState(null);
  const [busy, setBusy] = useState(null);
  const set = (k, v) => { setF((x) => ({ ...x, [k]: v })); setPreview(null); };

  useEffect(() => {
    if (f.frequency === "monthly") setF((x) => ({ ...x, first_period_start: `${year}-01-01`, first_pay_date: `${year}-01-31` }));
  }, [f.frequency, year]);

  const run = async (dry) => {
    setBusy(dry ? "preview" : "create");
    try {
      const r = await axios.post(`${API}/admin/payroll/${masterId}/runs/generate-year`, { year, ...f, dry_run: dry }, { headers: authH() });
      if (dry) setPreview(r.data);
      else { toast.success(`${r.data.created} période(s) créées en brouillon${r.data.skipped_existing ? ` · ${r.data.skipped_existing} déjà existante(s)` : ""}`); onDone(); }
    } catch (e) { err(e); } finally { setBusy(null); }
  };

  return (
    <div className="bg-white border-2 border-emerald-200 rounded-xl p-4 space-y-3" data-testid="paie-year-calendar-form">
      <div className="flex items-center gap-2 text-sm font-bold text-emerald-900"><CalendarClock className="w-4 h-4" /> Calendrier de paie {year}</div>
      <div className="grid sm:grid-cols-3 gap-3">
        <Field label="Fréquence">
          <select className={inputCls} value={f.frequency} onChange={(e) => set("frequency", e.target.value)} data-testid="paie-cal-frequency">
            {Object.entries(FREQ_LABELS).map(([k, v]) => <option key={k} value={k}>{v}</option>)}
          </select>
        </Field>
        <Field label="Début de la 1re période" hint="Peut être en décembre de l'année précédente"><input type="date" className={inputCls} value={f.first_period_start} onChange={(e) => set("first_period_start", e.target.value)} data-testid="paie-cal-start" /></Field>
        <Field label="1re date de paie" hint="Le délai est reconduit sur toutes les périodes"><input type="date" className={inputCls} value={f.first_pay_date} onChange={(e) => set("first_pay_date", e.target.value)} data-testid="paie-cal-pay-date" /></Field>
      </div>
      <div className="flex flex-wrap items-center gap-x-4 gap-y-1">
        <Check label="Décaler la paie au jour ouvrable précédent si elle tombe un férié ou une fin de semaine" checked={f.shift_holidays} onChange={(v) => set("shift_holidays", v)} testId="paie-cal-shift-holidays" />
        {!f.shift_holidays && <span className="text-[11px] text-slate-500">Les dates de paie sont gardées telles quelles (ex. 1er janvier). Chaque période reste modifiable après création.</span>}
      </div>
      {preview && (
        <div className="bg-emerald-50 border border-emerald-200 rounded-lg p-3 text-xs text-emerald-900 space-y-1" data-testid="paie-cal-preview">
          <p><b>{preview.count}</b> période(s) payées en {year} · <b>{preview.to_create}</b> à créer{preview.count - preview.to_create > 0 ? ` · ${preview.count - preview.to_create} déjà existante(s)` : ""}</p>
          {(preview.warnings || []).map((w, i) => (
            <div key={i} className="flex items-start gap-1.5 text-amber-900 bg-amber-50 border border-amber-200 rounded-lg px-2 py-1.5" data-testid={`paie-cal-warning-${i}`}><AlertTriangle className="w-3.5 h-3.5 mt-0.5 shrink-0" /><span>{w}</span></div>
          ))}
          <div className="max-h-48 overflow-y-auto divide-y divide-emerald-100">
            {preview.items.map((p) => (
              <div key={p.period_start} className="py-1 flex items-center gap-2 flex-wrap" data-testid={`paie-cal-row-${p.period_start}`}>
                <span className={`font-mono ${p.exists ? "line-through text-emerald-700/50" : ""}`}>{p.period_start} → {p.period_end}</span>
                <span>· paie le <b>{p.pay_date}</b>{p.pay_date_planned && <span className="text-amber-700"> (décalée du {p.pay_date_planned})</span>}{p.year !== year && <span className="ml-1 px-1.5 py-0.5 rounded-full bg-amber-200 text-amber-900 text-[10px] font-bold" data-testid={`paie-cal-year-${p.period_start}`}>année {p.year}</span>}</span>
                {p.holidays.map((h) => <span key={h.date} className="px-1.5 py-0.5 rounded-full bg-amber-100 text-amber-800 border border-amber-200 text-[10px] font-semibold">Férié {h.date.slice(5)} · {h.label}</span>)}
              </div>
            ))}
          </div>
        </div>
      )}
      <div className="flex flex-col-reverse sm:flex-row gap-2 sm:justify-end">
        <Btn tone="slate" icon={X} onClick={onCancel}>Annuler</Btn>
        <Btn tone="slate" icon={Eye} onClick={() => run(true)} busy={busy === "preview"} testId="paie-cal-preview-btn">Aperçu</Btn>
        <Btn tone="emerald" icon={Sparkles} onClick={() => run(false)} busy={busy === "create"} disabled={preview ? preview.to_create === 0 : false} testId="paie-cal-create-btn">Créer les périodes de {year}</Btn>
      </div>
    </div>
  );
}
